import { api, SERVER } from "../scripts/api.js";

/*
   <app-pack-units> - a pack's tabs, their answer keys, and Blank / Solution downloads (2026-10-16).
   Route: #pack-units/<classId>/<packId>, and EMBEDDED as the "Tabs and solutions" tab of the pack page
   (#pack-rules/<classId>/<packId>/units; <app-pack-units embedded>: no page heading of its own).

   David, 10/16: "We need a way for me to easily view the unit and download the solution
   version as well as the blank version from the portal."

   Server: routes/unit-view.js (teacher / TA of the class only).
     Blank    = the tab exactly as delivered, with the chosen scenario's game inputs filled in
     Solution = the same plus that scenario's answer key, and (when the unit has one) the
                worked formula for every game written beside the games, with the Why under it
   The answer key table here is the same key the add-in grades against.
*/

const esc = (s) => String(s ?? "").replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));
const fmt = (v) => (typeof v === "number" ? (Number.isInteger(v) ? v.toLocaleString("en-US") : String(v)) : esc(v ?? ""));

class AppPackUnits extends HTMLElement {
   connectedCallback() {
      if (this.dataset.built) return;              // the pack page moves it between views: build once
      this.dataset.built = "1";
      const embedded = this.hasAttribute("embedded");
      this.innerHTML = `
         <section id="pack-units">
            <div class="page-head" ${embedded ? "hidden" : ""}>
               <button type="button" id="pu-back">&larr; Class</button>
               <h1 id="pu-title">Tabs and solutions</h1>
               <p class="sub">Every tab in this pack: what it asks, the worked formula, and the answer key. Download any scenario as a Blank
                  (the tab with that scenario's inputs, as a student gets it) or a Solution (the same with the answers and the worked formula).</p>
            </div>
            <div class="pu-bar">
               <label>Scenario <select id="pu-scen"></select></label>
               <span class="pu-note">Each tab has 20 scenarios: the same questions on different game inputs. A student's tab uses one of them.</span>
            </div>
            <p class="msg" id="pu-msg"></p>
            <div id="pu-body"></div>
         </section>`;
      this.querySelector("#pu-back").addEventListener("click", () => {
         location.hash = this.classId ? `#class-detail/${this.classId}` : "#classes";
      });
      this.querySelector("#pu-scen").addEventListener("change", (e) => {
         this.scenario = Number(e.target.value);
         this.querySelectorAll(".pu-key[data-open='1']").forEach((k) => this.loadKey(k));
      });
      this.scenario = 1;
   }

   async show(param) {
      const [c, p] = String(param || "").split("/");
      const classId = Number(c), packId = Number(p);
      const body = this.querySelector("#pu-body");
      if (!Number.isInteger(classId) || !Number.isInteger(packId)) { body.innerHTML = `<p class="empty">No pack selected.</p>`; return; }
      this.classId = classId; this.packId = packId;
      body.innerHTML = `<p class="empty">Loading...</p>`;
      this.msg("");
      try {
         const res = await api(`/classes/${classId}/packs/${packId}/units`);
         const d = await res.json().catch(() => ({ ok: false, reason: `http_${res.status}` }));
         if (!d.ok) throw new Error(d.reason || "failed");
         this.data = d;
         this.render();
      } catch (e) {
         body.innerHTML = "";
         this.msg(`Could not load the tabs (${e.message}).`, "err");
      }
   }

   msg(t, kind = "") { const m = this.querySelector("#pu-msg"); m.textContent = t; m.className = `msg${kind ? " " + kind : ""}`; }

   render() {
      const d = this.data;
      if (!this.hasAttribute("embedded")) this.querySelector("#pu-title").textContent = `Tabs and solutions: ${d.pack.title}`;
      const maxScen = Math.max(1, ...d.units.map((u) => Number(u.scenario_count || 20)));
      const sel = this.querySelector("#pu-scen");
      sel.innerHTML = Array.from({ length: maxScen }, (_, i) => `<option value="${i + 1}" ${i + 1 === this.scenario ? "selected" : ""}>${i + 1}</option>`).join("");
      const body = this.querySelector("#pu-body");
      if (!d.units.length) { body.innerHTML = `<p class="empty">This pack has no fixed tabs to show (a draw pack picks its tabs per run).</p>`; return; }
      body.innerHTML = d.units.map((u) => this.card(u)).join("");
      body.querySelectorAll(".pu-dl").forEach((b) => b.addEventListener("click", () => this.download(b)));
      body.querySelectorAll(".pu-keybtn").forEach((b) => b.addEventListener("click", () => {
         const k = body.querySelector(`.pu-key[data-unit="${b.dataset.unit}"]`);
         const open = k.dataset.open === "1";
         k.dataset.open = open ? "0" : "1";
         b.textContent = open ? "Show answer key" : "Hide answer key";
         if (open) k.innerHTML = ""; else this.loadKey(k);
      }));
   }

   card(u) {
      const h = u.hints || {};
      const text = (u.text || []).map((l) => `<div class="pu-line"><span class="pu-row">${l.row}</span>${l.cells.map((c) => `<span class="pu-cell">${fmt(c.value)}</span>`).join("")}</div>`).join("");
      const meta = [u.tier ? `Tier ${u.tier}` : "", u.par_secs ? `par ${Math.round(u.par_secs / 60 * 10) / 10} min` : "",
                    `${u.questions} games, ${u.points} pts`, (u.skill_tags || []).join(", ")].filter(Boolean).join(" · ");
      return `
         <article class="pu-card">
            <header>
               <h2><span class="pu-pos">Tab ${u.position}</span> ${esc(u.sheet_name)}</h2>
               <div class="pu-meta">${esc(meta)}</div>
               <div class="pu-actions">
                  <button type="button" class="pu-dl" data-unit="${u.unit_id}" data-kind="blank" data-name="${esc(u.sheet_name)}" ${u.has_file ? "" : "disabled"}>Blank .xlsx</button>
                  <button type="button" class="pu-dl pu-sol" data-unit="${u.unit_id}" data-kind="solution" data-name="${esc(u.sheet_name)}" ${u.has_file ? "" : "disabled"}>Solution .xlsx</button>
                  ${u.has_guide ? `<button type="button" class="pu-dl" data-unit="${u.unit_id}" data-kind="guide" data-name="${esc(u.sheet_name)}" title="The one-page 'Show me how' a student can open in the add-in">Guide .pdf</button>` : ""}
                  <button type="button" class="pu-keybtn" data-unit="${u.unit_id}">Show answer key</button>
               </div>
            </header>
            ${u.error ? `<p class="msg err">${esc(u.error)}</p>` : ""}
            ${h.question ? `<p class="pu-q"><b>Question.</b> ${esc(h.question)}</p>` : ""}
            ${h.rule ? `<p class="pu-rule"><b>Rule.</b> ${esc(h.rule)}</p>` : ""}
            ${h.formula ? `<div class="pu-formula"><b>Worked formula</b> (&lt;row&gt; stands for the game's row)<code>${esc(h.formula)}</code></div>` : ""}
            ${h.why ? `<p class="pu-why"><b>Why.</b> ${esc(h.why)}</p>` : ""}
            ${h.without ? `<p class="pu-why"><b>Another way.</b> ${esc(h.without)}</p>` : ""}
            ${!h.formula ? `<p class="pu-note">No worked formula is stored for this tab yet; the answer key and both downloads still work.</p>` : ""}
            <details class="pu-text"><summary>The tab's own text (instructions, question, rule, headers, example)</summary>${text || "<p class='empty'>No text found.</p>"}</details>
            <div class="pu-key" data-unit="${u.unit_id}" data-open="0"></div>
         </article>`;
   }

   async loadKey(el) {
      const unit = el.dataset.unit;
      const seq = String((Number(el.dataset.seq) || 0) + 1);       // only the newest request may draw
      el.dataset.seq = seq;
      const u = (this.data.units || []).find((x) => String(x.unit_id) === unit);
      if (u && this.scenario > Number(u.scenario_count || 20)) {
         el.innerHTML = `<p class="empty">This tab has only ${u.scenario_count} scenarios.</p>`;
         return;
      }
      el.innerHTML = `<p class="empty">Loading scenario ${this.scenario}...</p>`;
      try {
         const res = await api(`/classes/${this.classId}/packs/${this.packId}/units/${unit}/key?scenario=${this.scenario}`);
         const k = await res.json().catch(() => ({ ok: false, reason: `http_${res.status}` }));
         if (el.dataset.seq !== seq) return;
         if (!k.ok) throw new Error(k.reason || "failed");
         if (k.layout === "list") {
            // Named inputs and separate questions (the exam tabs): two lists, labels down a column.
            el.innerHTML = `
               <p class="pu-keyhead">Answer key, scenario ${k.scenario}</p>
               <div class="pu-lists">
                  <div><h3 class="pu-sub">Inputs</h3>
                     <table class="cd-table pu-table pu-list"><thead><tr><th>Cell</th><th>Input</th><th class="pu-num">Value</th></tr></thead>
                     <tbody>${(k.inputs || []).map((x) => `<tr><td class="pu-cellref">${esc(x.cell)}</td><td>${esc(x.name)}</td><td class="pu-num">${fmt(x.value)}</td></tr>`).join("")}</tbody></table></div>
                  <div><h3 class="pu-sub">Answers</h3>
                     <table class="cd-table pu-table pu-list"><thead><tr><th>Cell</th><th>Question</th><th class="pu-ans pu-num">Answer</th><th class="pu-num">Pts</th></tr></thead>
                     <tbody>${k.games.map((g) => `<tr><td class="pu-cellref">${esc(g.cell)}</td><td>${esc(g.label || "")}</td><td class="pu-ans pu-num">${fmt(g.answer)}</td><td class="pu-num">${g.points ?? ""}</td></tr>`).join("")}</tbody></table></div>
               </div>`;
            return;
         }
         el.innerHTML = `
            <p class="pu-keyhead">Answer key, scenario ${k.scenario}</p>
            <div class="pu-scroll"><table class="cd-table pu-table">
               <thead><tr><th>Game</th><th>Cell</th>${k.headers.map((x) => `<th>${esc(x)}</th>`).join("")}<th class="pu-ans">Answer</th></tr></thead>
               <tbody>${k.games.map((g) => `<tr><td>${g.game}</td><td class="pu-cellref">${esc(g.cell)}</td>
                  ${k.headers.map((x) => `<td class="pu-num">${fmt(g.inputs[x])}</td>`).join("")}<td class="pu-ans pu-num">${fmt(g.answer)}</td></tr>`).join("")}</tbody>
            </table></div>`;
      } catch (e) {
         if (el.dataset.seq !== seq) return;
         el.innerHTML = `<p class="msg err">Could not load the answer key (${esc(e.message)}).</p>`;
      }
   }

   async download(btn) {
      const kind = btn.dataset.kind, unit = btn.dataset.unit;
      const old = btn.textContent;
      btn.disabled = true; btn.textContent = "Preparing...";
      try {
         const base = `${SERVER}/classes/${this.classId}/packs/${this.packId}/units/${unit}`;
         const res = await fetch(kind === "guide" ? `${base}/guide` : `${base}/file?kind=${kind}&scenario=${this.scenario}`,
            { credentials: "include" });
         if (!res.ok) {
            const d = await res.json().catch(() => ({}));
            throw new Error(d.reason || `http_${res.status}`);
         }
         const blob = await res.blob();
         const url = URL.createObjectURL(blob);
         const a = document.createElement("a");
         a.href = url;
         const name = (btn.dataset.name || "tab").replace(/[\\/:*?"<>|]+/g, "");
         a.download = kind === "guide" ? `${name} - how to.pdf`
            : `${name} - scenario ${this.scenario} - ${kind === "solution" ? "SOLUTION" : "BLANK"}.xlsx`;
         document.body.appendChild(a); a.click(); a.remove();
         setTimeout(() => URL.revokeObjectURL(url), 5000);
         this.msg("");
      } catch (e) {
         this.msg(`Download failed (${e.message}).`, "err");
      } finally {
         btn.disabled = false; btn.textContent = old;
      }
   }
}

customElements.define("app-pack-units", AppPackUnits);
